/**
 * Admin V3 — Booking refund modal (AV3 A-2)
 *
 * Operator processes a full or partial refund via
 * POST /payments/refund (RBAC: payment:refund).
 *
 * The existing backend refund authority is reused verbatim: it is atomic and
 * idempotent (X-Idempotency-Key auto-attached by useAdminMutation), append-only,
 * and self-validating (rejects already-refunded / over-original / over-remaining).
 * Amount left blank = full refund. Parity with the legacy V2 RefundModal.
 *
 * No new business logic. No backend change. Financial Core untouched — this is a
 * UI surface over the canonical /payments/refund route.
 */
import React, { useState } from 'react';
import { Loader2, X, RotateCcw } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import useAdminMutation from '../../shared/hooks/useAdminMutation';
import { useToast } from '../../shared/hooks/useToast';
import { t, DEFAULT_LOCALE } from '../../i18n';

const inputCls =
  'w-full rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-900 ' +
  'placeholder:text-slate-400 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100';

// Standard operator refund-reason vocabulary (codes sent to the backend `reason`
// string; labels localised). Mirrors the cancel/reassign typed-reason pattern.
const REFUND_REASONS = ['customer_request', 'service_issue', 'duplicate_charge', 'other'];

const BookingRefundModal = ({ open, bookingId, booking, payment, onClose, onDone, locale = DEFAULT_LOCALE }) => {
  const m = useAdminMutation('/payments/refund', 'post');
  const { showSuccess, showError } = useToast();

  const [mode, setMode] = useState('full'); // 'full' | 'partial'
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('customer_request');

  if (!open || !bookingId) return null;

  const original = Number(
    payment?.amount ?? booking?.pricing?.final_price ?? booking?.pricing?.estimated_price ?? 0,
  );
  const parsedAmount = Number(amount);
  const partialValid =
    mode === 'full' || (Number.isFinite(parsedAmount) && parsedAmount > 0 && parsedAmount <= original);
  const valid = Boolean(reason) && partialValid;

  const submit = () => {
    m.mutate(
      {
        booking_id: bookingId,
        amount: mode === 'partial' ? parsedAmount : null,
        reason: reason || 'customer_request',
      },
      {
        onSuccess: () => {
          showSuccess(t('bookings.refund.ok', locale));
          if (onDone) onDone();
          onClose();
        },
        onError: (e) => showError(e?.response?.data?.detail || t('bookings.refund.error', locale)),
      },
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="adminv3-booking-refund-modal">
      <div className="w-[520px] max-w-[94vw] rounded-2xl bg-white p-5 shadow-xl">
        <div className="mb-1 flex items-center justify-between">
          <h3 className="text-[17px] font-semibold text-slate-900">{t('bookings.refund.title', locale)}</h3>
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100" data-testid="adminv3-booking-refund-close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="mb-3 text-[12px] text-slate-500">
          {t('bookings.refund.description', locale).replace('{ref}', booking?.booking_reference || '')}
        </p>

        <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800" data-testid="adminv3-booking-refund-audit">
          {t('bookings.refund.auditWarning', locale)}
        </div>

        <div className="space-y-3">
          <div className="flex gap-4 text-[13px]">
            <label className="flex items-center gap-2 text-slate-700">
              <input type="radio" name="refund-mode" checked={mode === 'full'} onChange={() => setMode('full')} data-testid="adminv3-booking-refund-mode-full" />
              {t('bookings.refund.modeFull', locale)}
            </label>
            <label className="flex items-center gap-2 text-slate-700">
              <input type="radio" name="refund-mode" checked={mode === 'partial'} onChange={() => setMode('partial')} data-testid="adminv3-booking-refund-mode-partial" />
              {t('bookings.refund.modePartial', locale)}
            </label>
          </div>

          {mode === 'partial' && (
            <div>
              <label className="text-[12px] font-semibold text-slate-600">{t('bookings.refund.amountLabel', locale)}</label>
              <input
                type="number"
                min="0.01"
                step="0.01"
                max={original || undefined}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className={inputCls}
                data-testid="adminv3-booking-refund-amount"
              />
              <p className="mt-1 text-[11px] text-slate-400">
                {t('bookings.refund.amountHint', locale).replace('{max}', String(original))}
              </p>
            </div>
          )}

          <div>
            <label className="text-[12px] font-semibold text-slate-600">{t('bookings.refund.reasonLabel', locale)}</label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className={inputCls}
              data-testid="adminv3-booking-refund-reason"
            >
              {REFUND_REASONS.map((r) => (
                <option key={r} value={r}>{t(`bookings.refund.reason.${r}`, locale)}</option>
              ))}
            </select>
          </div>
        </div>

        {!valid && (
          <p className="mt-3 text-[12px] text-rose-600" data-testid="adminv3-booking-refund-missing">
            {t('bookings.refund.required', locale)}
          </p>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="adminv3-booking-refund-dismiss">
            {t('bookings.refund.cancel', locale)}
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={submit}
            disabled={!valid || m.loading}
            className="bg-amber-600 hover:bg-amber-700"
            data-testid="adminv3-booking-refund-confirm"
          >
            {m.loading && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
            <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
            {t('bookings.refund.confirm', locale)}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BookingRefundModal;
